import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ParsedFlightData } from '../Overview/Flights/hooks/types';
import { TravelerAssignment } from '../../Traveler/types';
import { ITransport } from '../Overview/Transport/types';
import { ParsedMobilityData } from '../Overview/Mobility/types';
import { ParsedAccomodation } from '../Overview/Accomodations/hooks/types';

interface VouchersParsingContextType {
  lastParseVoucherPayload: FormData | null;
  setLastParseVoucherPayload: (payload: FormData | null) => void;
  parsedFlight: ParsedFlightData | null;
  setParsedFlight: (flight: ParsedFlightData | null) => void;
  parsedTransport: ITransport | null;
  setParsedTransport: (transport: ITransport | null) => void;
  parsedMobility: ParsedMobilityData | null;
  setParsedMobility: (mobility: ParsedMobilityData | null) => void;
  parsedAccomodation: ParsedAccomodation | null;
  setParsedAccomodation: (accomodation: ParsedAccomodation | null) => void;
  updatePassengers: (
    travelerMappings: TravelerAssignment[],
    handleReUploadVoucher?: (parsedFlight: ParsedFlightData) => boolean | Promise<void>,
  ) => void;
  updateTransportTravelers: (
    travelerMappings: TravelerAssignment[],
    handleReUploadVoucher?: (transport: ITransport) => boolean | Promise<void>,
  ) => void;
  updateMobilityTravelers: (
    travelerMappings: TravelerAssignment[],
    handleReUploadVoucher?: (mobility: ParsedMobilityData) => boolean | Promise<void>,
  ) => void;
  clearVoucherContextState: () => void;
  documentId: number | null;
  setDocumentId: (documentId: number | null) => void;
}

const VouchersParsingContext = createContext<VouchersParsingContextType | undefined>(undefined);

export const VouchersParsingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lastParseVoucherPayload, setLastParseVoucherPayload] = useState<FormData | null>(null);
  const [parsedFlight, setParsedFlight] = useState<ParsedFlightData | null>(null);
  const [parsedTransport, setParsedTransport] = useState<ITransport | null>(null);
  const [parsedMobility, setParsedMobility] = useState<ParsedMobilityData | null>(null);
  const [parsedAccomodation, setParsedAccomodation] = useState<ParsedAccomodation | null>(null);
  const [documentId, setDocumentId] = useState<number | null>(null);

  // update flight passengers
  const updatePassengers = useCallback(
    (
      travelerMappings: TravelerAssignment[],
      handleReUploadVoucher?: (parsedFlight: ParsedFlightData) => boolean | Promise<void>,
    ) => {
      setParsedFlight((prev) => {
        if (!(prev as ParsedFlightData)?.flightLegs) return prev;

        const updatedFlightLegs = (prev as ParsedFlightData).flightLegs.map((leg) => {
          if (!leg.passengers) return leg;

          const updatedPassengers = leg.passengers.map((passenger, index) => {
            if (!passenger.passengerName) {
              return {
                ...passenger,
                travelerId: travelerMappings[index].travelerId,
                travelerName: travelerMappings[index].travelerName,
                photo: travelerMappings[index].photo,
                dob: travelerMappings[index].dob,
                passportNumber: travelerMappings[index].passportNumber,
              };
            }
            // Find matching traveler mapping by voucherTravelerName
            const matchingTraveler = travelerMappings.find(
              (mapping) => mapping.voucherTravelerName?.toLowerCase() === passenger.passengerName?.toLowerCase(),
            );

            if (matchingTraveler) {
              return {
                ...passenger,
                travelerId: matchingTraveler.travelerId,
                travelerName: matchingTraveler.travelerName,
                photo: matchingTraveler.photo,
                dob: matchingTraveler.dob,
                passportNumber: matchingTraveler.passportNumber,
              };
            }

            return passenger;
          });

          return {
            ...leg,
            passengers: updatedPassengers,
          };
        });

        const updatedFlight = {
          ...(prev as ParsedFlightData),
          flightLegs: updatedFlightLegs,
        } as ParsedFlightData;

        handleReUploadVoucher?.(updatedFlight);

        return updatedFlight;
      });
    },
    [],
  );

  // update transport travelers
  const updateTransportTravelers = useCallback(
    (
      travelerMappings: TravelerAssignment[],
      handleReUploadVoucher?: (transport: ITransport) => boolean | Promise<void>,
    ) => {
      setParsedTransport((prev) => {
        if (!prev?.transportLegs) return prev;

        const updatedTransportLegs = prev.transportLegs.map((leg) => {
          if (!leg.travelers) return leg;

          const updatedTravelers = leg.travelers.map((traveler) => {
            const matchingTraveler = travelerMappings.find(
              (mapping) => mapping.voucherTravelerName?.toLowerCase() === traveler.passengerName?.toLowerCase(),
            );

            if (matchingTraveler) {
              return {
                ...traveler,
                travelerId: matchingTraveler.travelerId,
                travelerName: matchingTraveler.travelerName,
                photo: matchingTraveler.photo,
                dob: matchingTraveler.dob,
                passportNumber: matchingTraveler.passportNumber,
              };
            }

            return traveler;
          });

          return {
            ...leg,
            travelers: updatedTravelers,
          };
        });

        const updatedTransport = {
          ...prev,
          transportLegs: updatedTransportLegs,
        } as ITransport;

        handleReUploadVoucher?.(updatedTransport);

        return updatedTransport;
      });
    },
    [],
  );

  // update mobility travelers
  const updateMobilityTravelers = useCallback(
    (
      travelerMappings: TravelerAssignment[],
      handleReUploadVoucher?: (mobility: ParsedMobilityData) => boolean | Promise<void>,
    ) => {
      setParsedMobility((prev) => {
        if (!prev?.travelers) return prev;

        const updatedTravelers = prev.travelers.map((traveler) => {
          const matchingTraveler = travelerMappings.find(
            (mapping) => mapping.voucherTravelerName?.toLowerCase() === traveler.voucherTravelerName?.toLowerCase(),
          );

          if (matchingTraveler) {
            return {
              ...traveler,
              travelerId: matchingTraveler.travelerId,
              name: matchingTraveler.travelerName,
              dob: matchingTraveler.dob,
              isMatched: true,
              photo: matchingTraveler.photo,
              passportNumber: matchingTraveler.passportNumber,
            };
          }

          return traveler;
        });

        const updatedMobility = {
          ...(prev as ParsedMobilityData),
          travelers: updatedTravelers,
        } as ParsedMobilityData;

        handleReUploadVoucher?.(updatedMobility);

        return updatedMobility;
      });
    },
    [],
  );

  const clearVoucherContextState = useCallback(() => {
    setParsedFlight(null);
    setParsedTransport(null);
    setParsedMobility(null);
    setParsedAccomodation(null);
    setLastParseVoucherPayload(null);
  }, []);

  return (
    <VouchersParsingContext.Provider
      value={{
        lastParseVoucherPayload,
        setLastParseVoucherPayload,
        parsedFlight,
        setParsedFlight,
        parsedTransport,
        setParsedTransport,
        parsedMobility,
        setParsedMobility,
        parsedAccomodation,
        setParsedAccomodation,
        updatePassengers,
        updateTransportTravelers,
        updateMobilityTravelers,
        clearVoucherContextState,
        documentId,
        setDocumentId,
      }}
    >
      {children}
    </VouchersParsingContext.Provider>
  );
};

export const useVouchersParsing = () => {
  const { t } = useTranslation();
  const context = useContext(VouchersParsingContext);
  if (context === undefined) {
    throw new Error(t('vouchers.errors.provider_required'));
  }
  return context;
};
